import type { DateRangePickerProps } from "@mui/x-date-pickers-pro";
import { DateRangePicker } from "src/components/DateRangePicker/DateRangePicker.tsx";
import * as styles from "./DateRangePickerInput.style.ts";
import React, { forwardRef, type ReactNode, type Ref, useId } from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";
import { type Dayjs } from "dayjs";
import type { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts/PickersShortcuts";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

export type DateRangePickerInputProps = DateRangePickerProps<any> & {
  label?: string;
  required?: boolean;
  helperText?: ReactNode;
  error?: string;
  isLoading?: boolean;
  floatingError?: boolean;
  shortcuts?: PickersShortcutsItem<
    [string | null | Dayjs, string | null | Dayjs]
  >[];
};

const DateRangePickerInputImpl = forwardRef(
  (
    {
      label,
      required,
      helperText,
      isLoading = false,
      floatingError = false,
      onChange,
      shortcuts = [],
      error,
      slotProps: slotPropsProp,
      ...rest
    }: DateRangePickerInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const slotProps = {
      ...slotPropsProp,
      actionBar: { actions: [] },
      shortcuts: {
        items: shortcuts,
      },
      field: {
        clearable: true,
      },
      textField: {
        error: !!error,
        ...slotPropsProp?.textField,
      },
    };
    const id = useId();
    return (
      <Skeleton isLoading={isLoading}>
        <div css={styles.root}>
          {label && (
            <InputLabel
              id={id}
              label={label}
              required={required}
              helperText={helperText}
            />
          )}
          <DateRangePicker
            inputRef={ref}
            css={styles.input}
            slotProps={slotProps}
            onChange={onChange}
            {...rest}
          />
          {!!error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    );
  },
);

export const DateRangePickerInput = DateRangePickerInputImpl;
